import { useEffect, useState } from "react";
import { MailNotificationActions } from "./MailNotificationActions";
import {
  loadSyncedMail,
  loadSyncedMailDetail,
  syncGmailIncremental,
  type SyncedMail,
} from "../../core-api/gmail";

export function SyncedMailList({ mailboxId }: { mailboxId: string }) {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<SyncedMail[]>([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<SyncedMail | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setSelected(null);
    void loadSyncedMail(mailboxId, page)
      .then((result) => {
        if (active) {
          setItems(result.items);
          setTotal(result.total);
        }
      })
      .catch(() => {
        if (active)
          setError(
            "邮件加载失败，请刷新重试；也可能是当前账号没有邮件查看权限。",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [mailboxId, page, revision]);
  const [selectionId, setSelectionId] = useState("");
  useEffect(() => {
    let active = true;
    setSelected(null);
    if (selectionId)
      void loadSyncedMailDetail(mailboxId, selectionId)
        .then((message) => {
          if (active) setSelected(message);
        })
        .catch(() => {
          if (active) setError("邮件详情加载失败，请重试。");
        });
    return () => {
      active = false;
    };
  }, [mailboxId, selectionId]);
  return (
    <section aria-label="已同步邮件列表" style={{ marginTop: 20 }}>
      <h3>已同步邮件（{total} 封）</h3>
      <p>这里显示已读入 CRM 的原始邮件，不代表已经确认为客户询单或完成分发。</p>
      <button
        type="button"
        disabled={syncing}
        onClick={() => {
          setSyncing(true);
          setError("");
          setSyncMessage("正在从 Gmail 获取最新邮件…");
          void syncGmailIncremental(mailboxId)
            .then(() => {
              setSyncMessage(
                "同步任务已提交。首次授权会读取最近 24 小时的收件箱邮件；列表将自动刷新，也可手动刷新查看。",
              );
              [5_000, 15_000, 30_000].forEach((delay) => {
                window.setTimeout(() => setRevision((x) => x + 1), delay);
              });
            })
            .catch(() => {
              setSyncMessage("");
              setError("同步提交失败，请确认邮箱仍处于已授权状态后重试。");
            })
            .finally(() => setSyncing(false));
        }}
        style={{
          background: "#155eef",
          border: 0,
          borderRadius: 8,
          color: "white",
          cursor: syncing ? "wait" : "pointer",
          fontWeight: 700,
          marginRight: 10,
          padding: "10px 16px",
        }}
      >
        {syncing ? "正在同步…" : "同步 Gmail 最新邮件"}
      </button>
      <button
        type="button"
        onClick={() => {
          setSelectionId("");
          setRevision((x) => x + 1);
        }}
      >
        刷新邮件列表
      </button>
      {syncMessage && <p role="status">{syncMessage}</p>}
      {loading ? (
        <p role="status">正在加载邮件…</p>
      ) : error ? (
        <p role="alert">{error}</p>
      ) : items.length === 0 ? (
        <p>暂无已同步邮件。</p>
      ) : (
        <ul style={{ paddingLeft: 20 }}>
          {items.map((mail) => (
            <li
              key={mail.id}
              style={{ padding: "12px 0", overflowWrap: "anywhere" }}
            >
              <button type="button" onClick={() => setSelectionId(mail.id)}>
                {mail.subject || "（无主题）"}
              </button>
              <div>
                {mail.sender.name || mail.sender.email || "未知发件人"} ·{" "}
                {mail.receivedAt
                  ? new Date(mail.receivedAt).toLocaleString("zh-CN")
                  : "时间未知"}
              </div>
            </li>
          ))}
        </ul>
      )}
      {total > 50 && (
        <div>
          <button
            type="button"
            disabled={page === 1 || loading}
            onClick={() => setPage((x) => x - 1)}
          >
            上一页
          </button>
          <span>第 {page} 页</span>
          <button
            type="button"
            disabled={page * 50 >= total || loading}
            onClick={() => setPage((x) => x + 1)}
          >
            下一页
          </button>
        </div>
      )}
      {selected && (
        <article
          aria-label="邮件详情"
          style={{ borderTop: "1px solid #ddd", marginTop: 16, paddingTop: 12 }}
        >
          <button type="button" onClick={() => setSelectionId("")}>
            关闭邮件
          </button>
          <h4>{selected.subject || "（无主题）"}</h4>
          <MailNotificationActions
            key={selected.id}
            mailboxId={mailboxId}
            messageId={selected.id}
          />
          <pre
            style={{
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              fontFamily: "inherit",
            }}
          >
            {selected.textBody || "这封邮件没有可显示的纯文本正文。"}
          </pre>
        </article>
      )}
    </section>
  );
}
